import React from "react";

import { Download } from "lucide-react";

import { ACCENT, Modal } from "./Chrome";
import SectionBody from "./SectionBody";
import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorDigestData } from "../../../Types/XFactorBriefing";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";
import {
  BAND_FILL,
  SECTION_LABEL,
  bandOf,
  buildFullBriefing,
  formatDate,
  headlineScores,
} from "../lib/derive";

/**
 * The whole briefing: Executive Summary first, then every section in display
 * order, as one continuous feed.
 *
 * The tabs are anchors into that feed, not a pane switcher — this is a "full"
 * briefing, so it should be readable end to end in one scroll, and the tab bar
 * tracks the scroll rather than replacing what is under it.
 *
 * The body is the shared `SectionBody`. xfactor states the invariant that a
 * section looks the same wherever it renders, and this modal and the section
 * detail modal are two of the three places it appears — sharing the component
 * is what keeps that true rather than a promise in a comment.
 */
const FullBriefingModal: React.FC<{
  open: boolean;
  onClose: () => void;
  accountName: string;
  digestDate?: string | null;
  data?: XFactorDigestData | null;
}> = ({ open, onClose, accountName, digestDate, data }) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const briefing = React.useMemo(
    () => buildFullBriefing(accountName, digestDate ?? null, data),
    [accountName, digestDate, data]
  );
  const panes = React.useMemo(
    () => [briefing.summaryDetail, ...briefing.sections],
    [briefing]
  );
  const [active, setActive] = React.useState("summary");
  const paneRefs = React.useRef<Record<string, HTMLElement | null>>({});

  // Reopening on a different account should start at the top again.
  React.useEffect(() => {
    if (open) setActive("summary");
  }, [open, accountName]);

  /**
   * Scroll-spy over the feed.
   *
   * The root is the viewport rather than the modal body: the modal is fixed and
   * never taller than 80vh, so viewport intersection is the same question and
   * does not require reaching into `Modal` for its scroll container. The margins
   * pick whichever section is crossing the upper fifth of the screen.
   */
  React.useEffect(() => {
    if (!open) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        const hit = entries
          .filter(e => e.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          )[0];
        const id = hit?.target.getAttribute("data-section");
        if (id) setActive(id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    Object.values(paneRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [open, panes]);

  const jump = (sectionId: string) => {
    setActive(sectionId);
    paneRefs.current[sectionId]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Heading badge uses Health (same as AI-GEN Health pill / List BH), not Overall.
  const heroScore = headlineScores(data).health;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2.5">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{
              backgroundColor: BAND_FILL[bandOf(heroScore, bhThresholds)],
            }}
          >
            {heroScore == null ? "—" : Math.round(heroScore)}
          </span>
          <span>
            Full Briefing (Business Health):{" "}
            <span style={{ color: ACCENT }}>{accountName}</span>
          </span>
          {/* xfactor exports a PDF here. That path is client-side and out of
              scope, so the control is disabled rather than absent — its
              presence is part of the layout. */}
          <button
            type="button"
            disabled
            title="PDF export is not available in this port"
            aria-label="Download briefing (unavailable)"
            className="rounded p-1 text-gray-300"
          >
            <Download size={14} />
          </button>
        </span>
      }
      subtitle={formatDate(digestDate) || digestDate}
    >
      {/* Underlined tabs, as xfactor renders them — not pills. Pinned to the
          top of the scroll area so they stay reachable down the feed; the
          negative offset counters the modal body's own top padding. */}
      <div className="sticky -top-5 z-20 -mx-6 mb-4 border-b border-gray-200 bg-white px-6 pt-5">
        <div className="flex flex-wrap gap-x-4">
          {panes.map(p => {
            const isActive = p.sectionId === active;
            return (
              <button
                key={p.sectionId}
                type="button"
                onClick={() => jump(p.sectionId)}
                className="-mb-px border-b-2 px-0.5 pb-2 text-sm transition-colors"
                style={{
                  borderColor: isActive ? ACCENT : "transparent",
                  color: isActive ? ACCENT : p.missing ? "#cbd5e1" : "#475569",
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {/* Tabs use the short labels; the section heading below
                    carries the long section title. */}
                {SECTION_LABEL[p.sectionId] || p.label}
              </button>
            );
          })}
        </div>
      </div>

      {panes.map((p, i) => (
        <section
          key={p.sectionId}
          data-section={p.sectionId}
          ref={el => {
            paneRefs.current[p.sectionId] = el;
          }}
          // Clears the sticky tab bar when a tab scrolls this into view.
          className={`scroll-mt-16 ${i ? "mt-8 border-t border-gray-100 pt-6" : ""}`}
        >
          <h3 className="mb-3 text-sm font-semibold" style={{ color: ACCENT }}>
            {p.label}
          </h3>
          <SectionBody detail={p} data={data} />
        </section>
      ))}
    </Modal>
  );
};

export default FullBriefingModal;
